import { useSyncExternalStore } from 'react';
import type { DrawBrush, DrawBrushes } from '@lichess-org/chessground/draw';
import type { BoardArrow } from './chessground';

/**
 * The board skin: the ONLY place board and arrow colours are defined.
 *
 * Every board is a `BoardView` (Chessground, §6.2), and every board style reads the
 * CSS custom properties this module writes on the document root, so choosing a skin
 * restyles every board on the site at once — builder, practice and game review —
 * including boards already on screen. Arrow colours reach Chessground as brushes,
 * and the review legends read the same `--arrow-*` properties, so an arrow and its
 * legend never disagree.
 *
 * The choice is a per-browser preference (like the engine settings): it is saved in
 * `localStorage`, never sent to the API, synced across tabs through the `storage`
 * event, and falls back to the default skin when storage is unavailable.
 *
 * Adding a skin = adding an entry to `BOARD_THEMES`. Do not hardcode board or arrow
 * colours in CSS or components.
 */

/** Arrow roles used by the product: prepared/good, played/bad, alternative, info. */
export type ArrowRole = BoardArrow['brush'];
export const ARROW_ROLES: readonly ArrowRole[] = ['green', 'red', 'blue', 'yellow'];

export interface BoardTheme {
  id: string;
  name: string;
  light: string;
  dark: string;
  lastMove: string;
  selected: string;
  /** Legal-destination dot. */
  moveDest: string;
  /** Coordinate text drawn on a light / dark square. */
  coordOnLight: string;
  coordOnDark: string;
  arrows: Record<ArrowRole, string>;
}

const CLASSIC_ARROWS: Record<ArrowRole, string> = {
  green: '#15781b',
  red: '#882020',
  blue: '#003088',
  yellow: '#e68f00',
};

export const BOARD_THEMES: readonly BoardTheme[] = [
  {
    id: 'slate',
    name: 'Slate',
    light: '#dbe5e4',
    dark: '#648985',
    lastMove: 'rgba(222, 238, 98, 0.6)',
    selected: 'rgba(54, 215, 183, 0.6)',
    moveDest: 'rgba(20, 85, 30, 0.5)',
    coordOnLight: '#183936',
    coordOnDark: '#f3faf9',
    arrows: CLASSIC_ARROWS,
  },
  {
    id: 'walnut',
    name: 'Walnut',
    light: '#f0d9b5',
    dark: '#b58863',
    lastMove: 'rgba(155, 199, 0, 0.41)',
    selected: 'rgba(20, 85, 30, 0.5)',
    moveDest: 'rgba(20, 85, 30, 0.5)',
    coordOnLight: '#946f51',
    coordOnDark: '#f0d9b5',
    arrows: CLASSIC_ARROWS,
  },
  {
    id: 'ice',
    name: 'Ice',
    light: '#dee3e6',
    dark: '#8ca2ad',
    lastMove: 'rgba(155, 199, 0, 0.41)',
    selected: 'rgba(20, 85, 30, 0.5)',
    moveDest: 'rgba(20, 85, 30, 0.5)',
    coordOnLight: '#5f7784',
    coordOnDark: '#f2f7fa',
    arrows: { ...CLASSIC_ARROWS, blue: '#1b3f8f' },
  },
  {
    id: 'tournament',
    name: 'Tournament',
    light: '#eeeed2',
    dark: '#769656',
    lastMove: 'rgba(246, 246, 105, 0.6)',
    selected: 'rgba(186, 202, 68, 0.7)',
    moveDest: 'rgba(0, 0, 0, 0.18)',
    coordOnLight: '#5f7f43',
    coordOnDark: '#eeeed2',
    // Dark-green arrows vanish on green squares: use a teal for the prepared move.
    arrows: { ...CLASSIC_ARROWS, green: '#0b6e6e' },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    light: '#d4d4d4',
    dark: '#8b8b8b',
    lastMove: 'rgba(214, 226, 95, 0.6)',
    selected: 'rgba(54, 170, 215, 0.55)',
    moveDest: 'rgba(0, 0, 0, 0.25)',
    coordOnLight: '#555555',
    coordOnDark: '#f4f4f4',
    arrows: CLASSIC_ARROWS,
  },
];

export const DEFAULT_BOARD_THEME_ID = 'slate';
export const BOARD_THEME_KEY = 'chessreps.boardTheme';

export function boardThemeById(id: unknown): BoardTheme {
  return (
    BOARD_THEMES.find((theme) => theme.id === id) ??
    BOARD_THEMES.find((theme) => theme.id === DEFAULT_BOARD_THEME_ID)!
  );
}

/** The CSS custom properties every board style reads. */
export function boardThemeCssVars(theme: BoardTheme): Record<string, string> {
  return {
    '--board-light': theme.light,
    '--board-dark': theme.dark,
    '--board-last-move': theme.lastMove,
    '--board-selected': theme.selected,
    '--board-move-dest': theme.moveDest,
    '--board-coord-on-light': theme.coordOnLight,
    '--board-coord-on-dark': theme.coordOnDark,
    ...Object.fromEntries(ARROW_ROLES.map((role) => [`--arrow-${role}`, theme.arrows[role]])),
  };
}

/** Write a skin onto the document (or another root) so every board follows it. */
export function applyBoardTheme(theme: BoardTheme, root: HTMLElement | null = rootElement()): void {
  if (!root) return;
  for (const [name, value] of Object.entries(boardThemeCssVars(theme))) {
    root.style.setProperty(name, value);
  }
  root.dataset.boardTheme = theme.id;
}

function rootElement(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.documentElement;
}

/**
 * Chessground brush name for an arrow role under a skin. Names are per skin because
 * Chessground caches drawn arrows and arrow-head markers by brush name: recolouring
 * one name in place would leave arrows already on the board in the old colour.
 */
export function brushName(theme: BoardTheme, role: ArrowRole): string {
  return `${theme.id}-${role}`;
}

/**
 * Brushes for a board: every skin's named brushes (so a name stays valid even when the
 * skin changes between two board updates), plus Chessground's built-in role names —
 * used for arrows people draw themselves with a right-click — in the current skin.
 */
export function chessgroundBrushes(active: BoardTheme = readBoardTheme()): DrawBrushes {
  const brush = (theme: BoardTheme, role: ArrowRole): DrawBrush => ({
    key: brushName(theme, role),
    color: theme.arrows[role],
    opacity: 1,
    lineWidth: 10,
  });
  const named = Object.fromEntries(
    BOARD_THEMES.flatMap((theme) => ARROW_ROLES.map((role) => [brushName(theme, role), brush(theme, role)])),
  );
  return {
    ...named,
    green: brush(active, 'green'),
    red: brush(active, 'red'),
    blue: brush(active, 'blue'),
    yellow: brush(active, 'yellow'),
  };
}

// -- preference store --------------------------------------------------------------

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

let current: BoardTheme | null = null;
const listeners = new Set<() => void>();

/** The chosen skin; stable identity until it changes. */
export function readBoardTheme(): BoardTheme {
  if (current === null) {
    let stored: string | null = null;
    try {
      stored = safeStorage()?.getItem(BOARD_THEME_KEY) ?? null;
    } catch {
      stored = null;
    }
    current = boardThemeById(stored);
  }
  return current;
}

/** Choose a skin: persist it (best effort), restyle every board, notify subscribers. */
export function writeBoardTheme(
  id: string,
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): BoardTheme {
  const next = boardThemeById(id);
  current = next;
  try {
    storage?.setItem(BOARD_THEME_KEY, next.id);
  } catch {
    // Unpersistable storage still keeps the choice for this session.
  }
  applyBoardTheme(next);
  for (const listener of [...listeners]) listener();
  return next;
}

export function subscribeBoardTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBoardTheme(): BoardTheme {
  return useSyncExternalStore(subscribeBoardTheme, readBoardTheme, readBoardTheme);
}

/** Apply the saved skin before the first paint (called once from `main.tsx`). */
export function initBoardTheme(): BoardTheme {
  const theme = readBoardTheme();
  applyBoardTheme(theme);
  return theme;
}

// Another tab chose a skin: follow it here too.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== BOARD_THEME_KEY) return;
    current = null;
    applyBoardTheme(readBoardTheme());
    for (const listener of [...listeners]) listener();
  });
}

/** Forget the in-memory choice (tests). */
export function resetBoardThemeCache(): void {
  current = null;
}
